import { useCallback, useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import toast from "components/partials/toast/toast";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import {
  useGetTouchpointContentTypesQuery,
  useClassTypeOptions,
  usePopulationTypeOptions,
} from "hooks/api/dictionary";

import { getActionTouchpointTypeLabel, getErrorMessage } from "utilities";

import { LoadingButtonState } from "ui/button";
import { OptionType } from "ui/select";
import { AddTouchpointFormData } from "components/modals/creative-modal/creative-modal-types";

import { ADD_TOUCHPOINT_FORM_VALIDATION_SCHEMA } from "components/modals/creative-modal/creative-modal-consts";
import { useProgramTemplatesOptions } from "components/modals/creative-modal/hooks/use-program-templates-options";
import { submitCreateTouchpointFromTemplate } from "components/modals/creative-modal/helpers/submit-create-touchpoint-from-template";
import { submitCreateBlankTouchpoint } from "components/modals/creative-modal/helpers/submit-create-blank-touchpoint";
import { TouchpointCreateRequestType } from "generated/schemas";
import { useCreateTouchpoint } from "hooks/api/use-touchpoints";

export const useAddTouchpoint = (
  isOpen: boolean,
  onClose?: () => void,
  onConfirmClose?: () => void,
  folderId?: string,
  type: TouchpointCreateRequestType = TouchpointCreateRequestType.LANDING_PAGE,
) => {
  const navigate = useNavigate();
  const currentClient = useCurrentClient();
  const defaultCampaign = useCurrentCampaign();

  const [isFormValid, setIsFormValid] = useState(false);
  const [submitState, setSubmitState] = useState<LoadingButtonState>(LoadingButtonState.Default);

  const defaultValues: Partial<AddTouchpointFormData> = useMemo(
    () => ({
      touchpointTemplateId: "",
      name: "",
      populationType: "",
      contentType: "",
      folderId: folderId,
      insightsActivator: true,
      classes: [],
      type: type,
    }),
    [folderId, type],
  );

  const methods = useForm<AddTouchpointFormData>({
    mode: "onChange",
    defaultValues: defaultValues,
    resolver: ADD_TOUCHPOINT_FORM_VALIDATION_SCHEMA,
  });

  const {
    reset,
    setValue,
    watch,
    formState: { isValid, isDirty },
  } = methods;

  const currentContentType = watch("contentType");

  const {
    templates: touchpointTemplates,
    isLoading: isLoadingTouchpointTemplates,
    templateSelectOptions: touchpointTemplateOptions,
  } = useProgramTemplatesOptions({
    clientId: currentClient?.id,
    touchpointType: type,
    enabled: isOpen,
  });

  const touchpointMutation = useCreateTouchpoint();

  // Use the current campaign for content types
  const selectedCampaign = defaultCampaign;

  const campaignObjective = selectedCampaign.campaignObjective || defaultCampaign.campaignObjective;

  const { data: contentTypes = [] } = useGetTouchpointContentTypesQuery(campaignObjective);
  const { classTypeOptions } = useClassTypeOptions(campaignObjective);
  const { populationTypeOptions } = usePopulationTypeOptions();

  const contentTypeOptions: OptionType[] = useMemo(() => {
    return contentTypes.map((type) => ({
      label: type.description,
      value: type.name,
    }));
  }, [contentTypes]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset(defaultValues);
      setSubmitState(LoadingButtonState.Default);
    }
    // eslint-disable-next-line
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen) return;
    if (folderId !== undefined) {
      setValue("folderId", folderId, { shouldValidate: true });
    }
  }, [folderId, isOpen, setValue]);

  // Handle content type auto-setting when content types change or modal opens
  useEffect(() => {
    if (!contentTypes?.length) return;
    if (!isOpen) return; // Only auto-set when modal is open

    // If there's only one content type, always set it to ensure it's in the form state
    if (contentTypes.length === 1) {
      if (currentContentType !== contentTypes[0].name) {
        setValue("contentType", contentTypes[0].name, {
          shouldValidate: true,
        });
      }
    }
  }, [contentTypes, currentContentType, setValue, isOpen]);

  useEffect(() => {
    setIsFormValid(isValid);
  }, [isValid, isDirty]);

  const handleCancelClick = () => {
    if (isDirty && onConfirmClose) {
      onConfirmClose();
    } else if (onClose) {
      onClose();
    }
  };

  const handleAddTouchpointSubmit = useCallback(
    async (data: AddTouchpointFormData) => {
      setSubmitState(LoadingButtonState.Loading);

      if (!data.contentType || !data.populationType) {
        setSubmitState(LoadingButtonState.Default);
        return;
      }

      try {
        const newTouchpointPath = data.touchpointTemplateId
          ? await submitCreateTouchpointFromTemplate({
              data,
              touchpointTemplates,
              clientId: currentClient.id!,
              campaignId: defaultCampaign.id,
            })
          : await submitCreateBlankTouchpoint({
              data,
              clientId: currentClient.id!,
              campaignId: defaultCampaign.id,
              createTouchpoint: touchpointMutation.mutateAsync,
            });
        if (newTouchpointPath) {
          toast.success({ data: { title: `${getActionTouchpointTypeLabel(type)} created` } });
          navigate(newTouchpointPath);
        } else {
          setSubmitState(LoadingButtonState.Default);
        }
      } catch (err) {
        toast.error({
          data: {
            title: `Failed to create ${getActionTouchpointTypeLabel(type).toLowerCase()}`,
            message: getErrorMessage(err?.response?.data),
          },
        });
        setSubmitState(LoadingButtonState.Default);
      }
    },
    [currentClient.id, defaultCampaign.id, touchpointTemplates, navigate, touchpointMutation, type],
  );

  return {
    methods,
    isFormValid,
    submitState,
    handleAddTouchpointSubmit,
    handleCancelClick,
    contentTypes,
    contentTypeOptions,
    touchpointTemplates,
    touchpointTemplateOptions,
    isLoadingTouchpointTemplates,
    classTypeOptions,
    populationTypeOptions,
  };
};
